import { useRef, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

const CATEGORIES = [
  { value: "lab_report", label: "Lab report" },
  { value: "imaging", label: "Imaging" },
  { value: "referral", label: "Referral" },
  { value: "discharge_summary", label: "Discharge summary" },
  { value: "other", label: "Other" },
] as const;

type Category = (typeof CATEGORIES)[number]["value"];

const ALLOWED = ["application/pdf", "image/jpeg", "image/png"] as const;
const MAX_BYTES = 25 * 1024 * 1024;

/** How long to wait for storage before calling it a failure. */
const STORAGE_TIMEOUT_MS = 90_000;

type Stage = "idle" | "asking" | "sending" | "recording";

const SAYS: Record<Stage, string> = {
  idle: "Upload",
  asking: "Preparing…",
  sending: "Sending…",
  recording: "Recording…",
};

/**
 * PUT the file to S3, reporting how much of it has gone.
 *
 * XMLHttpRequest rather than fetch, for the one thing fetch cannot do: report
 * upload progress. A doctor watching a 20 MB scan needs to know it is moving,
 * and "Uploading…" on its own is indistinguishable from a hang.
 *
 * It also carries a timeout. A request that never answers is the failure this
 * panel handled worst, because it looked exactly like one still working.
 */
function putToStorage(
  url: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    request.setRequestHeader("Content-Type", file.type);
    request.timeout = STORAGE_TIMEOUT_MS;

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(
        new Error(
          `Storage refused the file (${request.status}). Try again, and tell ` +
            `Healtrics if it keeps happening.`,
        ),
      );
    };
    // A browser never says why a cross-origin request failed, so this cannot
    // name the cause. It can at least name the step, which is more than
    // "something went wrong" and is usually enough to tell a dropped
    // connection from a refusal.
    request.onerror = () =>
      reject(new Error("The file could not reach storage. Check the connection."));
    request.ontimeout = () =>
      reject(new Error("Storage did not answer in time. Try again."));
    request.onabort = () => reject(new Error("The upload was stopped."));

    request.send(file);
  });
}

/**
 * Upload a document in the two steps the contract describes (CLN-F-002).
 *
 * The file goes straight from this browser to S3 with a signed link. It never
 * passes through the API, so a large scan does not tie up a request worker and
 * the bytes take the shortest path to storage.
 *
 * The size and type checks here are a courtesy, not a control. They fail fast
 * so a doctor is not left waiting on a 25 MB upload the server will refuse.
 * The server checks both again, and its answer is the one that counts.
 */
export function UploadPanel({
  patientId,
  onUploaded,
  canUpload,
}: {
  patientId: string;
  onUploaded: () => void;
  canUpload: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<Category>("lab_report");
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("idle");
  /** 0 to 100 while the bytes are moving, null the rest of the time. */
  const [sent, setSent] = useState<number | null>(null);

  if (!canUpload) return null;

  async function upload() {
    const file = fileInput.current?.files?.[0];
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    if (!ALLOWED.includes(file.type as (typeof ALLOWED)[number])) {
      setError("A clinic can upload a PDF, a JPEG, or a PNG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("The file is over the 25 MB limit.");
      return;
    }

    setError(null);

    // Everything below is wrapped, and the stage is cleared in `finally`.
    //
    // It was not, and the one call that can throw is the raw fetch to S3,
    // which is also the only call that leaves this origin. A rejected fetch
    // was discarded by the `void` at the click handler, so the stage stayed
    // "uploading" for good: the button disabled, no error shown, nothing
    // moving. A doctor could not tell that from a slow upload, and the only
    // way out was reloading the page.
    try {
      setStage("asking");

      // Step one: ask for a link that puts the file straight into S3.
      const presigned = await api.POST("/portal/patients/{id}/documents/presign", {
        params: { path: { id: patientId }, header: clinicHeader() },
        body: {
          filename: file.name,
          content_type: file.type as "application/pdf" | "image/jpeg" | "image/png",
          size_bytes: file.size,
        },
      });

      if (presigned.error || !presigned.data?.upload_url) {
        setError(readError(presigned.error).message);
        return;
      }

      // The signed link goes to S3, not to the API, so it carries no session.
      setStage("sending");
      setSent(0);
      await putToStorage(presigned.data.upload_url, file, setSent);

      // Step two: the file is there, so record it. The row starts unviewable
      // and a scan decides whether it becomes viewable or goes to quarantine.
      setStage("recording");
      setSent(null);
      const committed = await api.POST("/portal/documents/commit", {
        params: { header: clinicHeader() },
        body: {
          upload_token: presigned.data.upload_token,
          category,
          title: title.trim() || file.name,
        },
      });

      if (committed.error) {
        setError(readError(committed.error).message);
        return;
      }

      setTitle("");
      if (fileInput.current) fileInput.current.value = "";
      onUploaded();
    } catch (thrown) {
      setError(
        thrown instanceof Error && thrown.message
          ? thrown.message
          : "The upload did not finish. Check the connection and try again.",
      );
    } finally {
      setStage("idle");
      setSent(null);
    }
  }

  const busy = stage !== "idle";

  return (
    <div className="upload">
      <h2>Add a document</h2>

      <div className="row">
        <input ref={fileInput} type="file" accept={ALLOWED.join(",")} disabled={busy} />

        <select
          value={category}
          onChange={(event) => setCategory(event.target.value as Category)}
          disabled={busy}
          aria-label="Document type"
        >
          {CATEGORIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Title (optional)"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={busy}
        />

        <button type="button" onClick={() => void upload()} disabled={busy}>
          {sent === null ? SAYS[stage] : `Sending… ${sent}%`}
        </button>
      </div>

      {busy && (
        <div
          className="upload-progress"
          role="progressbar"
          aria-label="Upload progress"
          aria-valuemin={0}
          aria-valuemax={100}
          {...(sent === null ? {} : { "aria-valuenow": sent })}
        >
          {/* Indeterminate before and after the bytes move: the two steps
              around them are quick, and a bar that pretends to know how long
              they take is a bar that lies. */}
          <div
            className={sent === null ? "upload-bar upload-bar--waiting" : "upload-bar"}
            style={sent === null ? undefined : { width: `${sent}%` }}
          />
        </div>
      )}

      {/*
        Both halves are read now, and they are not read equally well. A report
        printed to PDF carries its own text and comes out exact. A scan or a
        photograph goes through OCR, which is good on a flat scan and poor on
        a photograph taken at an angle, so the review screen flags the rows it
        was unsure of (ADR-023). Saying "may need more correcting" sets the
        expectation a doctor should have before they start accepting values.
      */}
      <p className="muted">
        A new file is scanned before anyone can open it, then a lab report is read for
        measurements you can check. A photograph or a scan is read too, less exactly, so
        expect more to correct.
      </p>

      <ErrorNote message={error} />
    </div>
  );
}
